import { clusterDaysLeftRows } from "@/lib/cluster-days-left";
import { clusterAbbreviation } from "@/lib/cluster-short-names";
import type { OrderStats, Product } from "@/lib/product-types";
import { dataKey } from "@/lib/product-types";

/**
 * Колонка «Матрица кластеров»: одна ячейка со всеми кластерами товара —
 * чип на кластер, «заказы за 7 дней / остаток» (`Мск 12/40 · Кзн 5/0`).
 *
 * Состояние чипа говорит, где что не так:
 *  - alarm — заказывают, а остатка нет (продажи уходят в чужой кластер или
 *    срываются): красный;
 *  - idle — остаток лежит, а заказов нет: бледный;
 *  - normal — и заказы, и остаток.
 *
 * Порядок — по заказам, при равных по остатку. Остаток — free, как у «Хватит
 * дней» (тот же helper clusterDaysLeftRows, чтобы цифры двух колонок
 * совпадали); у строки модели заказы и остатки складываются по размерам.
 */

export const CLUSTER_MATRIX_KEY = "cluster_matrix";
export const CLUSTER_MATRIX_PERIOD = 7;

export type ClusterMatrixState = "alarm" | "idle" | "normal";

export type ClusterMatrixRow = {
  cluster: string;
  /** Короткая подпись чипа: «Мск». */
  short: string;
  orders: number;
  stock: number;
  /** Заказов в день за период. */
  speed: number;
  /** На сколько дней хватит остатка; null — заказов нет. */
  daysLeft: number | null;
  state: ClusterMatrixState;
};

function stateOf(orders: number, stock: number): ClusterMatrixState {
  if (orders > 0 && stock <= 0) return "alarm";
  if (orders <= 0 && stock > 0) return "idle";
  return "normal";
}

/**
 * Строки матрицы по товарам (у модели — сумма по размерам), отсортированные.
 * `stats` — заказы каждого товара по тому же индексу (как у ячеек календаря:
 * стабильные списки держат memo ячейки).
 */
export function clusterMatrixRows(products: readonly Product[], stats: ReadonlyArray<OrderStats | null | undefined>): ClusterMatrixRow[] {
  const totals = new Map<string, { orders: number; stock: number }>();
  for (let index = 0; index < products.length; index++) {
    const product = products[index];
    const stats_ = stats[index];
    if (!stats_ && !product.warehouses?.length) continue;
    const rows = clusterDaysLeftRows(product.warehouses, stats_ ?? EMPTY_STATS, CLUSTER_MATRIX_PERIOD);
    for (const row of rows) {
      const total = totals.get(row.cluster) ?? { orders: 0, stock: 0 };
      total.orders += row.orders;
      total.stock += row.stock;
      totals.set(row.cluster, total);
    }
  }
  return [...totals.entries()]
    .map(([cluster, { orders, stock }]) => {
      const speed = orders / CLUSTER_MATRIX_PERIOD;
      return {
        cluster,
        short: clusterAbbreviation(cluster),
        orders,
        stock,
        speed,
        daysLeft: speed > 0 ? Math.floor(stock / speed) : null,
        state: stateOf(orders, stock),
      };
    })
    .sort((a, b) => b.orders - a.orders || b.stock - a.stock || a.cluster.localeCompare(b.cluster, "ru"));
}

const EMPTY_STATS = { clusters_to_7d: {} } as unknown as OrderStats;

/** Заказы товаров из общей карты — для резолвера и группировки. */
export function clusterMatrixRowsFromOrderData(products: readonly Product[], orderData: Record<string, OrderStats>): ClusterMatrixRow[] {
  return clusterMatrixRows(products, products.map(product => orderData[dataKey(product)]));
}

/** Текст ячейки для экспорта и поиска: «Мск 12/40 · Кзн 5/0». */
export function clusterMatrixText(rows: readonly ClusterMatrixRow[]): string {
  return rows.map(row => `${row.short} ${row.orders}/${row.stock}`).join(" · ");
}

/**
 * Число для сортировки: сколько кластеров заказывают без остатка — сначала
 * самые проблемные строки; при равном числе выше та, где таких заказов больше.
 */
export function clusterMatrixSortValue(rows: readonly ClusterMatrixRow[]): number {
  let alarms = 0, lostOrders = 0;
  for (const row of rows) {
    if (row.state !== "alarm") continue;
    alarms += 1;
    lostOrders += row.orders;
  }
  return alarms * 1000 + Math.min(999, lostOrders);
}
